import React, { useEffect, useMemo, useState } from 'react';
import Spinner from '../../ui/Spinner';
import CorePhotoLightbox from '../../CorePhotoLightbox';
import { CorePhotoTileImage } from '../../CorePhotosGallery';
import drillingService from '../../../services/drillingService';
import { buildIntervalGroups } from '../../../utils/corePhotoCondition';

function parseDepth(v) {
  if (v == null || v === '') return NaN;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

/** Min/max depth from surrounding rows + optional fields on selected sample */
function computeDepthOverlapSpan(surroundingRows, selectedSample) {
  const depths = [];
  for (const row of surroundingRows || []) {
    const a = parseDepth(row.from_depth);
    const b = parseDepth(row.to_depth);
    if (!Number.isNaN(a)) depths.push(a);
    if (!Number.isNaN(b)) depths.push(b);
  }
  if (selectedSample) {
    const keys = [
      'from_depth',
      'to_depth',
      'depth_from',
      'depth_to',
      'from_depth_m',
      'to_depth_m'
    ];
    for (const k of keys) {
      const x = parseDepth(selectedSample[k]);
      if (!Number.isNaN(x)) depths.push(x);
    }
  }
  if (depths.length === 0) return null;
  let lo = Math.min(...depths);
  let hi = Math.max(...depths);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (lo === hi) {
    lo -= 0.05;
    hi += 0.05;
  }
  const pad = Math.max((hi - lo) * 0.02, 0.02);
  return { lo: lo - pad, hi: hi + pad };
}

function formatIntervalCaption(depthFrom, depthTo) {
  if (depthFrom != null && depthTo != null &&
      Number.isFinite(Number(depthFrom)) && Number.isFinite(Number(depthTo))) {
    return `${Number(depthFrom).toFixed(2)}–${Number(depthTo).toFixed(2)} m`;
  }
  return '—';
}

const SurroundingSamplesTable = ({ data, loading, selectedSample }) => {
  const [corePhotos, setCorePhotos] = useState([]);
  const [corePhotosLoading, setCorePhotosLoading] = useState(false);
  const [corePhotosError, setCorePhotosError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const holeId = useMemo(() => {
    const h = selectedSample?.hole_id || selectedSample?.holeId;
    return h != null && String(h).trim() !== '' ? String(h).trim() : null;
  }, [selectedSample]);

  const depthSpan = useMemo(
    () => (!loading && data && selectedSample ? computeDepthOverlapSpan(data, selectedSample) : null),
    [data, selectedSample, loading]
  );

  useEffect(() => {
    if (loading || !selectedSample || !holeId || !depthSpan) {
      setCorePhotos([]);
      setCorePhotosError(null);
      setCorePhotosLoading(false);
      return;
    }

    let cancelled = false;
    setCorePhotosLoading(true);
    setCorePhotosError(null);

    drillingService
      .getCorePhotos(holeId, {
        depthOverlapFrom: depthSpan.lo,
        depthOverlapTo: depthSpan.hi,
        limit: 500,
        includeRenditions: true,
        includeSas: true
      })
      .then((res) => {
        if (!cancelled) setCorePhotos(res.photos || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setCorePhotos([]);
          setCorePhotosError(err.message || String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setCorePhotosLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loading, selectedSample, holeId, depthSpan]);

  const intervalRows = useMemo(() => {
    const groups = buildIntervalGroups(corePhotos || []);
    const rows = [];
    for (const [, g] of groups) {
      const ref = g.all[0];
      const wetPhoto = g.wet[0] || null;
      const dryPhoto = g.dry[0] || null;
      const loneUnknown =
        !wetPhoto && !dryPhoto && g.unknown.length ? g.unknown[0] : null;
      rows.push({
        key:
          ref != null
            ? `${ref.id ?? ref.blob_path}-${g.all.length}`
            : `unk-${rows.length}`,
        depthFrom: ref?.depth_from_m,
        depthTo: ref?.depth_to_m,
        wetPhoto,
        dryPhoto,
        loneUnknown
      });
    }
    rows.sort((a, b) => {
      const da = Number(a.depthFrom);
      const db = Number(b.depthFrom);
      if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
      if (Number.isFinite(da) && !Number.isFinite(db)) return -1;
      if (!Number.isFinite(da) && Number.isFinite(db)) return 1;
      const ta = Number(a.depthTo);
      const tb = Number(b.depthTo);
      if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
      return 0;
    });
    return rows;
  }, [corePhotos]);

  const lightboxPhotos = useMemo(() => {
    const out = [];
    for (const r of intervalRows) {
      if (r.wetPhoto) out.push(r.wetPhoto);
      else if (r.loneUnknown) out.push(r.loneUnknown);
      if (r.dryPhoto) out.push(r.dryPhoto);
    }
    return out;
  }, [intervalRows]);

  const openLightbox = (photo) => {
    if (!photo) return;
    const idx = lightboxPhotos.findIndex(
      (p) =>
        (p.id != null && photo.id != null && p.id === photo.id) ||
        (p.blob_path && photo.blob_path && p.blob_path === photo.blob_path)
    );
    if (idx >= 0) {
      setLightboxIndex(idx);
      setLightboxOpen(true);
    }
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    const num = parseFloat(val);
    return isNaN(num) ? '-' : num.toFixed(5);
  };

  const formatDepth = (val) => {
    if (val === null || val === undefined) return '-';
    const num = parseFloat(val);
    return isNaN(num) ? '-' : num.toFixed(2);
  };

  const selectedKey = selectedSample?.sample_id ?? selectedSample?.sample_number;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '1rem' }}>
        <Spinner size="small" />
        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
          Loading surrounding samples...
        </p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <p style={{ color: '#9ca3af', fontSize: '0.875rem', fontStyle: 'italic' }}>
        No surrounding samples found
      </p>
    );
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h4 style={{ color: '#f9fafb', marginBottom: '0.75rem', fontSize: '1rem' }}>
        Surrounding Samples ({data.length})
      </h4>
      <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
        Samples before and after {selectedSample?.sample_number || selectedSample?.sample_id} for context
      </p>

      <div className="qaqc-surrounding-wrap">
        <div className="qaqc-surrounding-table-shell">
          <div style={{ overflowX: 'auto' }}>
            <table className="sample-details-table compact qaqc-surrounding-samples-table">
              <thead>
                <tr>
                  <th>Sample</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Type</th>
                  <th>Ctrl</th>
                  <th>Au</th>
                  <th>Ag</th>
                  <th>Cu</th>
                </tr>
              </thead>
              <tbody>
                {data.map((sample, idx) => {
                  const isSelected =
                    selectedKey != null &&
                    (sample.sample_id === selectedKey ||
                      String(sample.sample_id) === String(selectedKey));
                  return (
                    <tr
                      key={idx}
                      style={{
                        background: isSelected ? 'rgba(6, 182, 212, 0.15)' : undefined
                      }}
                    >
                      <td style={{ fontWeight: isSelected ? 'bold' : 'normal' }} title={sample.sample_id}>
                        {sample.sample_id}
                        {isSelected && ' ⬅'}
                      </td>
                      <td>{formatDepth(sample.from_depth)}</td>
                      <td>{formatDepth(sample.to_depth)}</td>
                      <td title={sample.sample_type || ''}>{sample.sample_type || '-'}</td>
                      <td title={sample.control_type || ''}>{sample.control_type || '-'}</td>
                      <td>{formatValue(sample.au_best)}</td>
                      <td>{formatValue(sample.ag_best)}</td>
                      <td>{formatValue(sample.cu_best)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="qaqc-surrounding-core-panel">
          <div className="qaqc-surrounding-core-title">Core photos</div>
          {!holeId && (
            <p className="qaqc-surrounding-core-note">
              No hole ID on the selected sample — core photos need a drill hole.
            </p>
          )}
          {holeId && !depthSpan && (
            <p className="qaqc-surrounding-core-note">
              No depth interval from surrounding samples — cannot scope core photos.
            </p>
          )}
          {holeId && depthSpan && (
            <>
              <div className="qaqc-surrounding-core-sub">
                {holeId} · {depthSpan.lo.toFixed(2)}–{depthSpan.hi.toFixed(2)} m
              </div>
              {corePhotosLoading && (
                <div className="qaqc-surrounding-core-loading">
                  <Spinner size="small" />
                  <span>Loading…</span>
                </div>
              )}
              {corePhotosError && !corePhotosLoading && (
                <p className="qaqc-surrounding-core-error">{corePhotosError}</p>
              )}
              {!corePhotosLoading && !corePhotosError && intervalRows.length === 0 && (
                <p className="qaqc-surrounding-core-note">No images for this depth window.</p>
              )}
              {!corePhotosLoading && !corePhotosError && intervalRows.length > 0 && (
                <div className="qaqc-surrounding-core-stack">
                  {intervalRows.map((row) => (
                    <div key={row.key} className="qaqc-surrounding-core-interval">
                      <div className="qaqc-surrounding-core-interval-cap">
                        {formatIntervalCaption(row.depthFrom, row.depthTo)}
                      </div>
                      <div className="qaqc-surrounding-core-pair">
                        <button
                          type="button"
                          className="qaqc-surrounding-core-thumb qaqc-surrounding-core-thumb-wet"
                          onClick={() =>
                            openLightbox(row.wetPhoto || row.loneUnknown)
                          }
                          disabled={!row.wetPhoto && !row.loneUnknown}
                          title="Wet (or unclassified)"
                        >
                          {row.wetPhoto || row.loneUnknown ? (
                            <CorePhotoTileImage
                              photo={row.wetPhoto || row.loneUnknown}
                              className="qaqc-surrounding-core-thumb-img"
                            />
                          ) : (
                            <span className="qaqc-surrounding-core-thumb-empty">—</span>
                          )}
                          <span className="qaqc-surrounding-core-thumb-label">
                            {row.loneUnknown && !row.wetPhoto ? '?' : 'Wet'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="qaqc-surrounding-core-thumb qaqc-surrounding-core-thumb-dry"
                          onClick={() => openLightbox(row.dryPhoto)}
                          disabled={!row.dryPhoto}
                          title="Dry"
                        >
                          {row.dryPhoto ? (
                            <CorePhotoTileImage
                              photo={row.dryPhoto}
                              className="qaqc-surrounding-core-thumb-img"
                            />
                          ) : (
                            <span className="qaqc-surrounding-core-thumb-empty">—</span>
                          )}
                          <span className="qaqc-surrounding-core-thumb-label">Dry</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CorePhotoLightbox
        photos={lightboxPhotos}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default SurroundingSamplesTable;
